import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { profileAvatar } from "@/data/mockData";
import {
  LayoutDashboard,
  UserCircle,
  Contact,
  GraduationCap,
  Briefcase,
  Code2,
  Sparkles,
  Trophy,
  MessageSquareQuote,
  FileText,
  Heart,
  Link2,
  Settings,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
  Circle,
  Award
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BACKEND_URL } from "@/config";


const navSections = [
  {
    label: "Workspace",
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }]
  },
  {
    label: "Profile",
    items: [
      { to: "/about", label: "About Me", icon: UserCircle },
      { to: "/personal", label: "Personal Information", icon: Contact },
      { to: "/education", label: "Education", icon: GraduationCap },
      { to: "/experience", label: "Experience", icon: Briefcase },
      { to: "/skills", label: "Technical Skills", icon: Code2 },
      { to: "/expertise", label: "Expertise", icon: Sparkles },
      { to: "/certifications", label: "Certifications", icon: Award }
    ]
  },
  {
    label: "Content",
    items: [
      { to: "/achievements", label: "Achievements", icon: Trophy },
      { to: "/testimonials", label: "Testimonials", icon: MessageSquareQuote },
      { to: "/blogs", label: "Blogs", icon: FileText },
      { to: "/interests", label: "Interests", icon: Heart },
      { to: "/social", label: "Social Links", icon: Link2 }
    ]
  },
  {
    label: "System",
    items: [{ to: "/settings", label: "Site Settings", icon: Settings }]
  }
];

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const { user } = useAuth();
  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        data-testid="sidebar"
        className={cn(
          "fixed lg:sticky top-0 z-50 h-screen flex-shrink-0 bg-sidebar border-r border-border transition-[width,transform] duration-300 ease-out",
          collapsed ? "lg:w-[76px]" : "lg:w-[260px]",
          "w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Workspace block */}
          <div
            className={cn(
              "relative flex items-center h-16 px-3 border-b border-border",
              collapsed ? "lg:justify-center lg:px-0" : "justify-between"
            )}
          >
            <div className={cn("flex items-center gap-2.5 min-w-0", collapsed && "lg:gap-0")}>
              <div className="relative shrink-0">
                <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-foreground text-background font-display font-bold text-lg">
                  F
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-sidebar" />
              </div>
              <div className={cn("min-w-0 leading-tight", collapsed ? "lg:hidden" : "block")}>
                <p className="font-display font-bold text-[13px] tracking-tight truncate">Folio Admin</p>
                <p className="text-[10px] font-mono text-muted-foreground truncate">workspace · pro</p>
              </div>
            </div>
            <button
              data-testid="sidebar-collapse-btn"
              onClick={() => setCollapsed(!collapsed)}
              className={cn(
                "hidden lg:inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
                collapsed && "lg:hidden"
              )}
              aria-label="Collapse sidebar"
            >
              <ChevronsLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>

          {collapsed && (
            <button
              data-testid="sidebar-expand-btn"
              onClick={() => setCollapsed(false)}
              className="hidden lg:flex h-8 w-8 mx-auto mt-2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              aria-label="Expand sidebar"
            >
              <ChevronsRight className="h-4 w-4" strokeWidth={1.5} />
            </button>
          )}

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto scrollbar-thin px-2.5 py-3 space-y-5">
            {navSections.map((section) => (
              <div key={section.label} className="space-y-0.5">
                <div
                  className={cn(
                    "px-2.5 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70",
                    collapsed ? "lg:hidden" : ""
                  )}
                >
                  {section.label}
                </div>
                {collapsed && (
                  <div className="hidden lg:flex justify-center mb-1">
                    <Circle className="h-1 w-1 fill-muted-foreground/40 text-muted-foreground/40" strokeWidth={0} />
                  </div>
                )}
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      title={collapsed ? item.label : undefined}
                      className={({ isActive }) =>
                        cn(
                          "group relative flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-all duration-150",
                          isActive
                            ? "bg-foreground text-background shadow-sm"
                            : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
                          collapsed && "lg:justify-center lg:px-0 lg:mx-auto lg:w-10 lg:h-10"
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon
                            className={cn(
                              "h-[18px] w-[18px] shrink-0 transition-colors",
                              isActive ? "" : "group-hover:text-foreground"
                            )}
                            strokeWidth={isActive ? 2 : 1.5}
                          />
                          <span
                            className={cn(
                              "truncate flex-1",
                              collapsed ? "lg:hidden" : "opacity-100"
                            )}
                          >
                            {item.label}
                          </span>
                          {isActive && !collapsed && (
                            <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 lg:block hidden" />
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Footer — User card */}
          <div className={cn("border-t border-border", collapsed ? "lg:p-2" : "p-3")}>
            {!collapsed ? (
              <a 
                href="https://shivajirajan.com/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block rounded-lg border border-border bg-card p-3 hover:border-foreground/20 transition-colors group cursor-pointer"
              >
                <div className="flex items-center gap-2.5">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.profileImageUrl ? (user.profileImageUrl.startsWith('http') ? user.profileImageUrl : `${BACKEND_URL}${user.profileImageUrl}`) : ""} alt={user?.username || "Admin"} />
                    <AvatarFallback className="text-[10px]">{user?.username?.substring(0, 2).toUpperCase() || "AM"}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate leading-tight">{user?.username || "Admin"}</p>
                    <p className="text-[10px] font-mono text-muted-foreground truncate">view public site</p>
                  </div>
                  <ExternalLink
                    className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0"
                    strokeWidth={1.5}
                  />
                </div>
              </a>
            ) : (
              <a 
                href="https://shivajirajan.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block mx-auto w-fit"
              >
                <Avatar className="h-9 w-9 cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2 hover:ring-offset-sidebar transition-all">
                  <AvatarImage src={user?.profileImageUrl ? (user.profileImageUrl.startsWith('http') ? user.profileImageUrl : `${BACKEND_URL}${user.profileImageUrl}`) : ""} alt={user?.username || "Admin"} />
                  <AvatarFallback className="text-[10px]">{user?.username?.substring(0, 2).toUpperCase() || "AM"}</AvatarFallback>
                </Avatar>
              </a>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
